import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { socureUploadDocument } from '../../store/actions/documents/documentsActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import { PAGES } from '../../constants/pages';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import DropzoneArea from '../../components/DropzoneArea';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  DOCUMENT_TYPES,
  KYC_DOCUMENT_TYPES,
  KYC_PERSON_TARGET_TYPES,
} from '../../util/constants';
import {
  ACCOUNT_TYPE_GTM,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import Paragraph from '../../components/Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { separateByUppercaseAndJoinedWithHorLine } from '../../util/helpers/stringHelpers';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

const DropZoneWrapperPassport = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KycUploadIdentityDocuments = ({ applicantType, documentType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData();
  const history = useHistory();
  const selectedDocument = retrieveFromSessionStorage(KYC_SELECTED_OPTION);
  const [errors, setErrors] = useState('');
  const { BrokerName } = useSelector(selectSettings);
  const isPassport = documentType === 'Passport';
  const [uploadedDocuments, setUploadedDocuments] = useState(
    isPassport
      ? { Document: '' }
      : {
          DocumentFront: '',
        },
  );
  const getTitleTag = () => {
    switch (documentType) {
      case DOCUMENT_TYPES.DRIVERS_LICENSE:
        return t('kycVerification.titleTagDriversLicense', {
          brokerName: BrokerName,
        });

      case DOCUMENT_TYPES.PASSPORT:
        return t('kycVerification.titleTagPassport', {
          brokerName: BrokerName,
        });

      case DOCUMENT_TYPES.GOVERNMENT_ID:
        return t('kycVerification.titleTagOther', {
          brokerName: BrokerName,
        });

      default:
        break;
    }
  };

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc documents upload',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc documents upload',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onSubmit = () => {
    const formData = new FormData();
    if (isPassport) {
      formData.append('Document', uploadedDocuments.Document);
    } else {
      formData.append('DocumentFront', uploadedDocuments.DocumentFront);
    }
    formData.append(
      'Caption',
      KYC_DOCUMENT_TYPES[`${selectedDocument.DocumentType}`],
    );

    const onSuccess = () => {
      handleGtmEvent();
      history.push(PAGES.KYC_VERIFICATION);
    };
    const params = {
      personTargetType:
        KYC_PERSON_TARGET_TYPES[selectedDocument?.PersonTargetType],
      documentType: KYC_DOCUMENT_TYPES[`${selectedDocument?.DocumentType}`],
      socureDocumentType: selectedDocument.SocureDocumentType,
    };

    dispatch(
      socureUploadDocument({
        ApplicationUid,
        params,
        data: formData,
        onSuccess,
        setErrors,
      }),
    );
  };

  const getPreviousPath = () =>
    PAGES.KYC_PROOF_OF_IDENTITY[String(applicantType).toUpperCase()].UPLOAD[
      String(separateByUppercaseAndJoinedWithHorLine(documentType))
    ];

  return (
    <BaseLayout previousPath={getPreviousPath()} isKycProcess="true" noLogoBox>
      <Helmet>
        <title>{getTitleTag()}</title>
      </Helmet>
      <Header
        titleMb={32}
        title={
          isPassport
            ? t('kycVerification.proofOfIdentityTitlePassport')
            : t('kycVerification.proofOfIdentityTitle')
        }
      />
      {isPassport ? (
        <DropZoneWrapperPassport>
          <DropzoneArea
            isPassport
            pdfFormatDisabled
            onUpload={(file) =>
              setUploadedDocuments({
                ...uploadedDocuments,
                Document: file,
              })
            }
          />
        </DropZoneWrapperPassport>
      ) : (
        <DropZoneWrapperPassport>
          <DropzoneArea
            frontSide
            pdfFormatDisabled
            onUpload={(file) =>
              setUploadedDocuments({
                ...uploadedDocuments,
                DocumentFront: file,
              })
            }
          />
        </DropZoneWrapperPassport>
      )}
      <Paragraph color={themeColors.colorErrorMessage} variant="normal">
        {errors}
      </Paragraph>
      <OnBoardingCta
        disabled={
          isPassport
            ? !uploadedDocuments.Document
            : !uploadedDocuments.DocumentFront
        }
        onClick={onSubmit}
      />
    </BaseLayout>
  );
};

KycUploadIdentityDocuments.propTypes = {
  applicantType: PropTypes.string,
  documentType: PropTypes.string,
};

export default KycUploadIdentityDocuments;
